import { useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import type { NavItem } from "./nav";

interface Props {
  item: NavItem;
  collapsed?: boolean;
  prefetch?: (to: string) => Promise<unknown> | void;
}

/* Panel rows: 38px, 10px radius. The current page is a tinted well with a
   small bearing dot on the right — not an edge bar, not a filled pill. */
const ROW = "group relative flex h-[38px] w-full items-center gap-3 rounded-control pl-3 pr-2.5 text-[13px] transition-colors duration-feedback ease-brand";
const ROW_IDLE = "text-fg-muted hover:bg-bg-elevated hover:text-fg";
const ROW_ACTIVE = "bg-primary-soft text-fg font-semibold";

function ActiveEdge() {
  return <span aria-hidden className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.18)]" />;
}

export function SidebarLink({ item, collapsed, prefetch }: Props) {
  const location = useLocation();
  const hasChildren = !!item.children?.length;

  const childActive =
    hasChildren && item.children!.some((c) => c.to && location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);

  const onMouseEnter = useCallback(() => {
    if (item.to && prefetch) prefetch(item.to);
    if (hasChildren) item.children!.forEach((c) => c.to && prefetch?.(c.to));
  }, [item, prefetch, hasChildren]);

  const Icon = item.icon;

  /* ─── Leaf ─────────────────────────────────────────────────────── */
  if (!hasChildren && item.to) {
    return (
      <NavLink
        to={item.to}
        onMouseEnter={onMouseEnter}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) => cn(ROW, isActive ? ROW_ACTIVE : ROW_IDLE, collapsed && "justify-center px-0")}
      >
        {({ isActive }) => (
          <>
            {Icon && <Icon className={cn("h-[17px] w-[17px] shrink-0", isActive ? "text-primary" : "text-fg-subtle group-hover:text-fg-muted")} strokeWidth={1.6} />}
            {!collapsed && <span className="truncate">{item.label}</span>}
            {isActive && !collapsed && !item.badge && <ActiveEdge />}
            {!collapsed && item.badge && (
              <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  }

  /* ─── Branch ───────────────────────────────────────────────────── */
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={onMouseEnter}
        aria-expanded={open}
        title={collapsed ? item.label : undefined}
        className={cn(ROW, childActive ? "text-fg font-medium" : ROW_IDLE, collapsed && "justify-center px-0")}
      >
        {Icon && <Icon className={cn("h-[17px] w-[17px] shrink-0", childActive ? "text-primary" : "text-fg-subtle group-hover:text-fg-muted")} strokeWidth={1.6} />}
        {!collapsed && (
          <>
            <span className="truncate">{item.label}</span>
            <ChevronDown
              className={cn("ml-auto h-4 w-4 shrink-0 text-fg-subtle transition-transform duration-popover ease-brand", open && "rotate-180")}
              strokeWidth={1.5}
            />
          </>
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && !collapsed && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
            className="overflow-hidden"
          >
            <li className="ml-[20px] mt-0.5 border-l border-dashed border-border-control/50 pl-3">
              <ul className="space-y-0.5 py-0.5">
                {item.children!.map((child) => (
                  <li key={child.label}>
                    <NavLink
                      to={child.to ?? "#"}
                      onMouseEnter={() => child.to && prefetch?.(child.to)}
                      className={({ isActive }) =>
                        cn(
                          "relative flex h-8 items-center rounded-lg px-2.5 text-[12.5px] transition-colors duration-feedback ease-brand",
                          isActive
                            ? "bg-primary-soft font-semibold text-fg before:absolute before:-left-[13px] before:top-1/2 before:h-[7px] before:w-[7px] before:-translate-y-1/2 before:rounded-full before:bg-primary"
                            : "text-fg-muted hover:bg-bg-elevated hover:text-fg",
                        )
                      }
                    >
                      <span className="truncate">{child.label}</span>
                      {child.badge && (
                        <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                          {child.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
